/**
 * Login (App) — E-Mail UND Passwort direkt eingeben.
 * Vor dem Login prueft access-check, ob ein aktiver Zugang mit gesetztem Passwort
 * existiert. Erstregistrierung bleibt bewusst im Web/Desktop.
 */
import { useEffect, useState } from "preact/hooks";
import { useLocation } from "preact-iso";
import { endpoints } from "../api/endpoints";
import { login } from "../state/auth";
import { ApiError } from "../api/client";
import { isAuthenticated } from "../state/session";
import { config } from "../config";
import { BrandHeader } from "../app/BrandHeader";
import { PasswordField } from "../ui/PasswordField";

const LOGIN_VIMEO = "https://player.vimeo.com/video/1198459737?fl=ml&fe=ec";
// Registrierung läuft im Web (nicht in der App).
const WEB_REGISTER_URL = `${config.apiBaseUrl}/register.html`;
const DESKTOP_REGISTER_HINT =
  "Diese E-Mail-Adresse ist in der App noch nicht als registrierter Kurszugang vorhanden. Die Erstregistrierung ist nur im Web auf der Desktop-Seite möglich.";
const DESKTOP_PASSWORD_HINT =
  "Für diese E-Mail-Adresse ist noch kein Passwort angelegt. Bitte richte dein Konto einmalig im Web auf der Desktop-Seite ein und melde dich danach hier in der App an.";

export function Login() {
  const { route } = useLocation();

  // Bereits eingeloggt → direkt in die App.
  useEffect(() => {
    if (isAuthenticated.value) route("/", true);
  }, []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(() => {
    const hint = sessionStorage.getItem("ki-kurs-login-hinweis");
    if (hint) sessionStorage.removeItem("ki-kurs-login-hinweis");
    return hint;
  });
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: Event) {
    e.preventDefault();
    const key = email.trim().toLowerCase();
    setError(null);
    setOkMsg(null);
    setBusy(true);
    try {
      const access = await endpoints.accessCheck(key);
      if (!access.allowed) {
        setError(access.message || DESKTOP_REGISTER_HINT);
        return;
      }
      if (!access.password_set) {
        setError(DESKTOP_PASSWORD_HINT);
        return;
      }
      await login(key, password);
      route("/");
    } catch (err) {
      let msg =
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : "E-Mail oder Passwort ist nicht korrekt.";
      if (err instanceof ApiError && (err.status === 401 || err.status === 400 || err.status === 404)) {
        msg = "E-Mail oder Passwort ist nicht korrekt. Falls du noch kein Passwort hast, ist die Erstregistrierung nur im Web auf der Desktop-Seite möglich.";
      }
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function onForgot(e: Event) {
    e.preventDefault();
    const key = email.trim().toLowerCase();
    setError(null);
    if (!key) {
      setError("Bitte gib zuerst deine E-Mail-Adresse ein.");
      return;
    }
    try {
      await endpoints.passwordResetRequest(key);
    } catch {
      /* generische Antwort — Existenz nicht verraten */
    }
    setOkMsg(
      "Falls für diese E-Mail ein Zugang besteht, haben wir dir einen Link zum Zurücksetzen geschickt. Bitte prüfe dein Postfach.",
    );
  }

  return (
    <main class="auth-screen">
      <div class="auth-stack">
        <div class="login-video glass">
          <div class="video-frame">
            <iframe
              src={LOGIN_VIMEO}
              title="Einführung zum Sentinus KI-Kurs"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              allowFullScreen
            />
          </div>
        </div>

        <div class="glass login-card">
          <BrandHeader />
          <h1>Anmelden</h1>
          <p class="sub">Melde dich mit deiner E-Mail und deinem Passwort an.</p>

          <form onSubmit={onSubmit} autocomplete="on">
            <label>
              E-Mail-Adresse
              <input
                type="email"
                inputMode="email"
                autocomplete="email"
                placeholder="name@firma.de"
                required
                value={email}
                onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
              />
            </label>

            <label>
              Passwort
              <PasswordField
                value={password}
                onInput={setPassword}
                autocomplete="current-password"
                required
              />
            </label>

            <button class="btn" type="submit" disabled={busy}>
              {busy ? "Bitte warten …" : "Anmelden"}
            </button>

            <p class="hint forgot-row">
              <a href="#" onClick={onForgot}>
                Passwort vergessen?
              </a>
            </p>

            {error ? <p class="err">{error}</p> : null}
            {okMsg ? <p class="ok-msg" aria-live="polite">{okMsg}</p> : null}
          </form>

          <p class="hint">
            Noch keinen Zugang oder noch kein Passwort? Die <strong>Erstregistrierung
            läuft nur im Web auf der Desktop-Seite</strong> — danach meldest du dich
            hier in der App an.{" "}
            <a href={WEB_REGISTER_URL} target="_blank" rel="noopener">Im Web registrieren</a>.
          </p>
          <p class="hint">
            Zugang erhältst du vom Sentinus-Team. Dein Fortschritt wird unter deiner
            E-Mail gespeichert.
          </p>
        </div>
      </div>
    </main>
  );
}
